import React from 'react';
import { useModal } from 'react-modal-hook';
import { Avatar } from 'components';
import { Appearance, Size } from 'design/types';
import { ILabor, ILaborLocation, PermissionType } from 'types';
import { hasSitePermission } from 'utils/employers';
import { AddShiftModal } from './AddShiftModal';
import { EditShiftModal } from './EditShiftModal';
import { RestoreShiftModal } from './RestoreShiftModal';
import { ShiftRow } from './ShiftRow';
import {
  EmployeeCardActionGroup,
  EmployeeCardActions,
  EmployeeCardHeader,
  EmployeeCardLayout,
  EmployeeDuration,
  EmployeeEmptyMessage,
  EmployeeEmptyState,
  EmployeeIdentity,
  EmployeeName,
  EmployeeNameText,
  EmployeeSubtext,
  ManualOverrideTag,
  ManualOverrideTooltip,
  ReviewTag,
  RowButton,
  Separator,
  ShiftsLayout,
} from './styles';
import { formatOverriddenBy, getEmployeeDurationLabel, getEmployeeLocationKey, scheduleNeedsReview } from './utils';

interface IEmployeeShiftCardProps {
  siteId: string;
  date: string;
  // Where the labor is recorded (or, without any, where it's added), and whose timezone and business day it's shown and saved in.
  location: ILaborLocation;
  employeeId: string;
  employeeName?: string;
  labor?: ILabor;
  jobTitles: string[];
  permissions?: PermissionType[];
}

export const EmployeeShiftCard: React.FC<IEmployeeShiftCardProps> = ({ siteId, date, location, employeeId, employeeName, labor, jobTitles, permissions }) => {
  const { id: locationId, timezone, businessDayStart } = location;
  const schedules = labor?.schedules || [];
  const isOverridden = Boolean(labor?.overridden);
  const needsReview = schedules.some(scheduleNeedsReview);
  const isMissingClockOut = schedules.some((schedule) => !schedule.end);
  const hasSchedules = schedules.length > 0;
  const canUpdateLaborData = hasSitePermission(permissions, PermissionType.INTEGRATION_POS_LABOR_DATA_UPDATE);
  // The job worked when there's a single shift, otherwise how many shifts there are.
  const shiftsSummary = schedules.length > 1 ? `${schedules.length} shifts` : schedules[0]?.jobTitle;

  const [showEditModal, hideEditModal] = useModal(
    () => (
      <EditShiftModal
        siteId={siteId}
        date={date}
        locationId={locationId}
        timezone={timezone}
        businessDayStart={businessDayStart}
        employeeId={employeeId}
        employeeName={employeeName}
        labor={labor}
        jobTitles={jobTitles}
        onClose={hideEditModal}
      />
    ),
    [siteId, date, locationId, timezone, businessDayStart, employeeId, employeeName, labor, jobTitles]
  );

  const [showRestoreModal, hideRestoreModal] = useModal(
    () => <RestoreShiftModal siteId={siteId} date={date} locationId={locationId} employeeId={employeeId} employeeName={employeeName} labor={labor} onClose={hideRestoreModal} />,
    [siteId, date, locationId, employeeId, employeeName, labor]
  );

  const [showAddModal, hideAddModal] = useModal(
    () => (
      <AddShiftModal
        siteId={siteId}
        date={date}
        locationId={locationId}
        timezone={timezone}
        businessDayStart={businessDayStart}
        employeeId={employeeId}
        employeeName={employeeName}
        labor={labor}
        jobTitles={jobTitles}
        onClose={hideAddModal}
      />
    ),
    [siteId, date, locationId, timezone, businessDayStart, employeeId, employeeName, labor, jobTitles]
  );

  return (
    <EmployeeCardLayout data-cy="employee-shift-card">
      <EmployeeCardHeader>
        <EmployeeIdentity>
          <Avatar size="small" username={employeeName || employeeId} />
          <EmployeeName>
            <EmployeeNameText data-cy="employee-name">{employeeName || '[Unknown]'}</EmployeeNameText>
            <EmployeeSubtext>
              <span data-cy="employee-id">ID {employeeId}</span>
              {shiftsSummary && (
                <>
                  {' '}
                  <Separator>·</Separator> <span data-cy="employee-shifts-summary">{shiftsSummary}</span>
                </>
              )}
            </EmployeeSubtext>
          </EmployeeName>
        </EmployeeIdentity>

        <EmployeeCardActions>
          <EmployeeCardActionGroup>
            {isOverridden && (
              <ManualOverrideTag
                dataCy="manual-override-tag"
                label="Manual override"
                appearance={Appearance.WARNING}
                size={Size.SMALL}
                capitalize={false}
                tooltipId={`manual-override-${getEmployeeLocationKey(locationId, employeeId)}`}
                tooltipPlace="top"
                tooltipMessage={
                  <ManualOverrideTooltip>
                    <span data-cy="manual-override-reason">Reason: {labor.overridden.reason}</span>
                    <span data-cy="manual-override-by">{formatOverriddenBy(labor.overridden)}</span>
                  </ManualOverrideTooltip>
                }
              />
            )}
            {!isOverridden && needsReview && <ReviewTag dataCy="needs-review-tag" label={isMissingClockOut ? 'Missing clock out' : 'Missing break end'} appearance={Appearance.DANGER} size={Size.SMALL} capitalize={false} />}
            <EmployeeDuration data-cy="employee-duration">{hasSchedules || isOverridden ? getEmployeeDurationLabel(labor) : '—'}</EmployeeDuration>
          </EmployeeCardActionGroup>
          {canUpdateLaborData && (hasSchedules || isOverridden) && (
            <EmployeeCardActionGroup>
              {hasSchedules && <RowButton appearance="white" size="small" label="Edit" onClick={showEditModal} data-cy="edit-shift-button" />}
              {isOverridden && <RowButton appearance="white" size="small" label="Restore" onClick={showRestoreModal} data-cy="restore-shift-button" />}
            </EmployeeCardActionGroup>
          )}
        </EmployeeCardActions>
      </EmployeeCardHeader>

      {hasSchedules ? (
        <ShiftsLayout>
          {schedules.map((schedule, index) => (
            <ShiftRow key={`${schedule.jobTitle}-${schedule.start}-${index}`} schedule={schedule} timezone={timezone} />
          ))}
        </ShiftsLayout>
      ) : (
        <EmployeeEmptyState>
          <EmployeeEmptyMessage data-cy="employee-empty-message">
            {isOverridden ? 'All labor removed for this business day — this employee will be excluded from the tip calculation.' : 'No labor data received from Legion for this employee.'}
          </EmployeeEmptyMessage>
          {canUpdateLaborData && <RowButton appearance="white" size="small" label="Add labor" onClick={showAddModal} data-cy="add-labor-button" />}
        </EmployeeEmptyState>
      )}
    </EmployeeCardLayout>
  );
};
